/**
 * /weather — Weather Almanac (Phase 1).
 *
 * Sections rendered in order:
 *   1. Locality header + last-refreshed indicator
 *   2. Today card (hero: temp, condition, sunrise/sunset, moon phase, wind, humidity)
 *   3. Historical Comparison (Today vs Normal vs Record)
 *   4. Weather Timeline (last 30 days as scrollable strip)
 *   5. Weather Calendar (color-coded month view, prev/next month nav)
 *   6. Monthly Summary
 *   7. Search bar (region / event / date / free-text)
 *
 * All data pulled from `/api/weather-almanac/*` endpoints — each hit is served
 * from Mongo cache when possible.
 */
import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLocality } from "@/contexts/LocalityContext";
import {
  Cloud, Sun, CloudRain, Snowflake, Wind, Droplets, Sunrise, Sunset, Moon,
  Loader2, Search, Calendar as CalendarIcon, ChevronLeft, ChevronRight, MapPin,
  AlertTriangle, TrendingUp, ThermometerSun, ThermometerSnowflake, Trophy,
  Sprout, Bug, Flower2, Activity, Camera, ThumbsUp, Send, Users, ShieldCheck,
  Fish, Moon as MoonIcon, CloudDrizzle, Video, Link2, StickyNote, X,
  ChevronDown,
} from "lucide-react";
import WeatherMap from "@/components/WeatherMap";
import WeatherAssistant from "@/components/WeatherAssistant";
import WeatherHero from "@/components/WeatherHero";
import LocationConfirmBar from "@/components/LocationConfirmBar";
import PushOptIn from "@/components/PushOptIn";

const CATEGORY_COLORS = {
  severe:   { bg: "#7c3aed", fg: "#fff",     label: "Severe" },
  storm:    { bg: "#4b5563", fg: "#fff",     label: "Storm" },
  snow:     { bg: "#e0f2fe", fg: "#0369a1",  label: "Snow" },
  rain:     { bg: "#3b82f6", fg: "#fff",     label: "Rain" },
  hot:      { bg: "#dc2626", fg: "#fff",     label: "Hot" },
  warm:     { bg: "#fbbf24", fg: "#78350f",  label: "Warm" },
  pleasant: { bg: "#22c55e", fg: "#052e16",  label: "Pleasant" },
  cool:     { bg: "#d1d5db", fg: "#111827",  label: "Cool" },
  cold:     { bg: "#0ea5e9", fg: "#fff",     label: "Cold" },
};

const EVENT_ICONS = {
  thunderstorm: "⛈", lightning: "⚡", heavy_rain: "🌧", rain: "🌧", light_rain: "🌦",
  blizzard: "❄", snowstorm: "🌨", snowfall: "🌨", ice_storm: "🧊", hail: "🌨",
  fog: "🌫", tornado: "🌪", hurricane: "🌀", cyclone: "🌀", dust_storm: "🌪",
  wildfire_smoke: "🔥", extreme_heat: "🔥", heat_wave: "☀", extreme_cold: "🥶",
  frost: "❄", high_wind: "💨", flood_risk: "🌊",
};

export default function WeatherAlmanac() {
  const { locality } = useLocality();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [coords, setCoords] = useState(null);
  const [today, setToday] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [calMonth, setCalMonth] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
  });
  const [calendar, setCalendar] = useState(null);
  const [summary, setSummary] = useState(null);
  const [advisories, setAdvisories] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  // Nudge counter — bumped whenever any "Report Local Weather" CTA is
  // clicked so the CommunityReports child opens its form modal.
  const [reportRequestTick, setReportRequestTick] = useState(0);

  /** Central handler for every "Report Local Weather" CTA on the page.
   *  - Not signed in → redirect to /login with a `next` back to /weather?report=1
   *    so login auto-brings the user back to the form.
   *  - Signed in → bump `reportRequestTick`, which the CommunityReports
   *    child listens to and opens the modal.
   *  Also always scrolls the report section into view first so the modal
   *  drops into the correct place even if it renders below the fold.
   */
  const openReport = useCallback(() => {
    if (authLoading) return;
    if (!user) {
      const next = "/weather?report=1";
      navigate(`/login?next=${encodeURIComponent(next)}`);
      return;
    }
    setReportRequestTick((t) => t + 1);
    // Small delay so the modal appears above the section it belongs to.
    setTimeout(() => {
      document.getElementById("community-reports")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  }, [authLoading, user, navigate]);

  // Auto-open the form if the user just returned from login via ?report=1.
  useEffect(() => {
    if (authLoading) return;
    const params = new URLSearchParams(location.search);
    if (params.get("report") !== "1") return;
    if (!user) return; // still not signed in — leave the param, user will re-click.
    // Trigger the open, then strip the param so refresh doesn't re-fire.
    setReportRequestTick((t) => t + 1);
    setTimeout(() => {
      document.getElementById("community-reports")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
    const cleaned = new URLSearchParams(location.search);
    cleaned.delete("report");
    const suffix = cleaned.toString();
    navigate(`${location.pathname}${suffix ? `?${suffix}` : ""}${location.hash || ""}`, { replace: true });
  }, [authLoading, user, location.pathname, location.search, location.hash, navigate]);

  // Resolve coords: prefer locality, else geolocation, else NYC fallback.
  useEffect(() => {
    if (locality?.lat && locality?.lng) {
      setCoords({ lat: locality.lat, lng: locality.lng });
      return;
    }
    if (typeof navigator === "undefined" || !("geolocation" in navigator)) {
      setCoords({ lat: 40.7128, lng: -74.006 });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => setCoords({ lat: 40.7128, lng: -74.006 }),
      { timeout: 8000 },
    );
  }, [locality]);

  // Fetch primary data whenever coords change.
  useEffect(() => {
    if (!coords) return;
    let cancelled = false;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const [t, tl, cmp] = await Promise.all([
          http.get(`/weather-almanac/daily?lat=${coords.lat}&lng=${coords.lng}`),
          http.get(`/weather-almanac/timeline?lat=${coords.lat}&lng=${coords.lng}&days=30`),
          http.get(`/weather-almanac/comparison?lat=${coords.lat}&lng=${coords.lng}`),
        ]);
        if (!cancelled) {
          setToday(t.data);
          setTimeline(tl.data.days || []);
          setComparison(cmp.data);
        }
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Couldn't load weather data.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [coords]);

  // Fetch calendar + monthly summary when month or coords change.
  useEffect(() => {
    if (!coords) return;
    let cancelled = false;
    (async () => {
      try {
        const [c, s] = await Promise.all([
          http.get(`/weather-almanac/calendar?lat=${coords.lat}&lng=${coords.lng}&year=${calMonth.year}&month=${calMonth.month}`),
          http.get(`/weather-almanac/monthly-summary?lat=${coords.lat}&lng=${coords.lng}&year=${calMonth.year}&month=${calMonth.month}`),
        ]);
        if (!cancelled) { setCalendar(c.data); setSummary(s.data); }
      } catch { /* ignore, calendar is best-effort */ }
    })();
    return () => { cancelled = true; };
  }, [coords, calMonth]);

  // Advisories (Farmer / Gardening / Pest / Allergy / Activities).
  useEffect(() => {
    if (!coords) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await http.get(`/weather-almanac/advisories?lat=${coords.lat}&lng=${coords.lng}`);
        if (!cancelled) setAdvisories(data);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [coords]);

  const monthLabel = useMemo(
    () => new Date(calMonth.year, calMonth.month - 1, 1).toLocaleString("en", { month: "long", year: "numeric" }),
    [calMonth],
  );
  const changeMonth = (delta) => {
    setCalMonth((prev) => {
      const d = new Date(prev.year, prev.month - 1 + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() + 1 };
    });
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="weather-almanac-page">
      <div className="overline">SOCIAL PULSE · WEATHER ALMANAC</div>
      <h1
        className="font-display text-4xl sm:text-5xl lg:text-6xl mt-2 font-bold"
        style={{ color: "var(--wofy-primary)" }}
        data-testid="weather-page-title"
      >
        🌦️ Social Pulse Weather Almanac
      </h1>
      <p
        className="mt-3 text-xl sm:text-2xl font-display"
        style={{ color: "var(--wofy-text)" }}
        data-testid="weather-page-subtitle"
      >
        Your Community. Your Weather. Your History.
      </p>
      <p className="mt-4 max-w-3xl text-sm leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
        The Social Pulse Weather Almanac is a living record of your community's weather,
        created by the people who experience it every day. More than just a forecast, it
        documents local weather conditions, unusual events, seasonal changes, and long-term
        trends that become valuable for future generations.{" "}
        <Link
          to="/weather/about"
          className="underline font-medium"
          style={{ color: "var(--wofy-primary)" }}
          data-testid="weather-explore-link"
        >
          Explore »
        </Link>
      </p>

      <div className="mt-5 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={openReport}
          className="wofy-btn-primary"
          data-testid="weather-report-cta-top"
        >
          🌦️ Report Local Weather →
        </button>
        <a
          href="#weather-advisories"
          className="wofy-btn-ghost text-sm"
          data-testid="weather-advisories-cta"
          onClick={(e) => {
            e.preventDefault();
            document.getElementById("weather-advisories")?.scrollIntoView({ behavior: "smooth" });
          }}
        >
          🌡️ Today's Weather Advisory →
        </a>
        <Link
          to="/nature?section=weather&tab=forecast"
          className="wofy-btn-ghost text-sm"
          data-testid="weather-forecast-link"
        >
          See the 5-day forecast →
        </Link>
        <Link
          to="/almanac/map"
          className="wofy-btn-ghost text-sm"
          data-testid="weather-almanac-map-link"
        >
          🗺️ Explore Almanac Map →
        </Link>
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/weather-almanac/records.rss`}
          target="_blank"
          rel="noopener noreferrer"
          className="wofy-btn-ghost text-sm"
          data-testid="weather-almanac-rss-link"
        >
          📡 RSS Feed →
        </a>
        <a
          href="#all-weather-reports"
          className="wofy-btn-ghost text-sm"
          data-testid="weather-all-reports-anchor"
          onClick={(e) => {
            e.preventDefault();
            document.getElementById("all-weather-reports")?.scrollIntoView({ behavior: "smooth" });
          }}
        >
          🚨 All Weather Alert Reports →
        </a>
      </div>

      {/* Compelling "Join the Community Weather Team" hero — colorful,
          with live platform stats, recent-activity strip and a
          3-step "how you can help" card. */}
      <WeatherHero />

      <PushOptIn />

      {/* Locality strip */}
      <div className="mt-6 flex items-center gap-2 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
        <MapPin className="w-4 h-4" />
        <span>
          {today?.city || locality?.city || "Detecting…"}
          {today?.region ? `, ${today.region}` : ""}
          {today?.country ? `, ${today.country}` : ""}
        </span>
      </div>

      {loading && (
        <div className="mt-8 flex items-center gap-2 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
          <Loader2 className="w-4 h-4 animate-spin" /> Loading weather…
        </div>
      )}

      {err && (
        <div className="mt-6 rounded-lg border p-4 flex items-start gap-3" style={{ borderColor: "var(--wofy-border)" }}>
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <p className="text-sm font-medium">Couldn't load weather</p>
            <p className="text-xs mt-1" style={{ color: "var(--wofy-text-soft)" }}>{err}</p>
          </div>
        </div>
      )}

      {today && !loading && (
        <>
          <WeatherPageQuickNav
            sections={[
              { key: "today",     anchor: "sec-today",     label: "Today",     emoji: "☀️" },
              { key: "compare",   anchor: "sec-compare",   label: "Comparison", emoji: "📊" },
              { key: "advisory",  anchor: "sec-advisory",  label: "Advisories", emoji: "⚠️" },
              { key: "timeline",  anchor: "sec-timeline",  label: "Timeline",  emoji: "📅" },
              { key: "calendar",  anchor: "sec-calendar",  label: "Month",     emoji: "🗓️" },
              { key: "map",       anchor: "sec-map",       label: "Map",       emoji: "🗺️" },
              { key: "community", anchor: "sec-community", label: "Reports",   emoji: "👥" },
              { key: "assistant", anchor: "sec-assistant", label: "Ask AI",    emoji: "✨" },
              { key: "search",    anchor: "sec-search",    label: "Archive",   emoji: "🔎" },
              { key: "all",       anchor: "all-weather-reports", label: "All Reports", emoji: "🚨" },
            ]}
          />
          <SevereBanner events={today.events} category={today.category} />

          <CollapsibleSection storageKey="today" anchor="sec-today" title="Today's weather" icon={Sun} defaultOpen preview={today.summary || `${Math.round(today.temp_max_f || 0)}° / ${Math.round(today.temp_min_f || 0)}°`}>
            <TodayHero today={today} />
          </CollapsibleSection>

          <CollapsibleSection storageKey="compare" anchor="sec-compare" title="How does today compare?" icon={TrendingUp} defaultOpen={false} preview="Historical averages, records & anomalies">
            <Comparison cmp={comparison} />
          </CollapsibleSection>

          <CollapsibleSection storageKey="advisory" anchor="sec-advisory" title="Today's advisories" icon={AlertTriangle} defaultOpen preview={(advisories?.length ? `${advisories.length} advisory item${advisories.length===1?"":"s"}` : "No active advisories")}>
            <Advisories advisories={advisories} onReport={openReport} />
          </CollapsibleSection>

          <CollapsibleSection storageKey="timeline" anchor="sec-timeline" title={`Last ${timeline?.length || 30} days`} icon={CalendarIcon} defaultOpen={false} preview="Rolling weather timeline">
            <Timeline days={timeline} onPick={() => window.scrollTo({ top: 0, behavior: "smooth" })} />
          </CollapsibleSection>

          <CollapsibleSection storageKey="calendar" anchor="sec-calendar" title={monthLabel || "Month at a glance"} icon={CalendarIcon} defaultOpen={false} preview="Month-view calendar + summary">
            <CalendarGrid
              monthLabel={monthLabel}
              calendar={calendar}
              onPrev={() => changeMonth(-1)}
              onNext={() => changeMonth(1)}
              coords={coords}
            />
            <MonthlySummary summary={summary} monthLabel={monthLabel} />
          </CollapsibleSection>

          <CollapsibleSection storageKey="map" anchor="sec-map" title="Local weather map" icon={MapPin} defaultOpen={false} preview="Community reports plotted within 50 km">
            <WeatherMap coords={coords} radiusKm={50} />
          </CollapsibleSection>

          <CollapsibleSection storageKey="community" anchor="sec-community" title="Community reports nearby" icon={Users} defaultOpen preview="Read + share what people around you are seeing">
            <CommunityReports
              coords={coords}
              today={today}
              openTick={reportRequestTick}
              onReport={openReport}
            />
          </CollapsibleSection>

          <CollapsibleSection storageKey="assistant" anchor="sec-assistant" title="Ask the Weather Assistant" icon={Sun} defaultOpen={false} preview="Free-form AI answers grounded in your locality's live weather" cta="Grounded in live weather">
            <WeatherAssistant coords={coords} />
          </CollapsibleSection>
        </>
      )}

      <CollapsibleSection storageKey="search" anchor="sec-search" title="Search the locality archive" icon={Search} defaultOpen={false} preview="Locality-specific historical extremes by date, region, event type">
        <SearchPanel />
      </CollapsibleSection>
      <AllReportsPanel />
    </div>
  );
}

// ---------------- Sub-components ----------------

/**
 * CollapsibleSection — accordion wrapper for the big /weather sections.
 * Remembers the user's expand/collapse choice per-section in
 * localStorage so grandparents don't have to re-open sections on
 * every visit. `preview` renders a compact 1-line teaser when
 * collapsed so the value of the section is still visible.
 */
function CollapsibleSection({
  storageKey, title, icon: Icon, defaultOpen = false,
  preview, children, anchor, cta,
}) {
  const [open, setOpen] = useState(() => {
    try {
      const v = localStorage.getItem(`sp.weather.section.${storageKey}`);
      if (v === "1") return true;
      if (v === "0") return false;
    } catch {/* ignore */}
    return defaultOpen;
  });
  // React to WeatherPageQuickNav events so tapping a top-level tab
  // guarantees the section is expanded before the smooth-scroll lands.
  useEffect(() => {
    const onOpen = (e) => {
      if (e?.detail?.key === storageKey) setOpen(true);
    };
    window.addEventListener("sp.weather.open-section", onOpen);
    return () => window.removeEventListener("sp.weather.open-section", onOpen);
  }, [storageKey]);
  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      try { localStorage.setItem(`sp.weather.section.${storageKey}`, next ? "1" : "0"); } catch {/* ignore */}
      return next;
    });
  };
  return (
    <section
      id={anchor}
      className={"rounded-2xl border overflow-hidden mb-4 " + (open ? "shadow-sm" : "")}
      style={{ borderColor: "var(--wofy-border)", background: "white", scrollMarginTop: "80px" }}
      data-testid={`section-${storageKey}`}
    >
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition"
        data-testid={`section-${storageKey}-toggle`}
      >
        {Icon && <Icon className="w-4 h-4 shrink-0" style={{ color: "var(--wofy-primary)" }} />}
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg leading-tight" style={{ color: "var(--wofy-text)" }}>{title}</div>
          {!open && preview && (
            <div className="text-xs mt-0.5 truncate" style={{ color: "var(--wofy-text-soft)" }}>{preview}</div>
          )}
        </div>
        {cta && <span className="text-xs opacity-70 hidden sm:inline">{cta}</span>}
        <ChevronDown
          className={"w-4 h-4 shrink-0 transition-transform " + (open ? "rotate-180" : "")}
          style={{ color: "var(--wofy-text-soft)" }}
        />
      </button>
      {open && (
        <div className="border-t px-4 py-4" style={{ borderColor: "var(--wofy-border)" }} data-testid={`section-${storageKey}-body`}>
          {children}
        </div>
      )}
    </section>
  );
}

/**
 * WeatherPageQuickNav — floating anchor tabs at the top of /weather so
 * visitors can jump straight to any section without hunting through
 * the accordions. Every entry maps to a `CollapsibleSection`'s
 * `anchor` and auto-opens that section when clicked.
 */
function WeatherPageQuickNav({ sections }) {
  const jump = (s) => {
    try { localStorage.setItem(`sp.weather.section.${s.key}`, "1"); } catch {/* ignore */}
    const el = document.getElementById(s.anchor);
    if (el) {
      // Force expand: toggle open synchronously via a "sectionopen" event.
      window.dispatchEvent(new CustomEvent("sp.weather.open-section", { detail: { key: s.key } }));
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };
  return (
    <div
      className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b -mx-4 px-4 py-2 mb-4 overflow-x-auto"
      style={{ borderColor: "var(--wofy-border)" }}
      data-testid="weather-quicknav"
    >
      <div className="flex gap-1.5 whitespace-nowrap">
        {sections.map((s) => (
          <button
            key={s.key}
            onClick={() => jump(s)}
            className="px-3 py-1.5 rounded-full text-xs border hover:bg-slate-50 transition"
            style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text)" }}
            data-testid={`weather-quicknav-${s.key}`}
          >
            {s.emoji} {s.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function TodayHero({ today }) {
  const cat = CATEGORY_COLORS[today.category] || CATEGORY_COLORS.pleasant;
  return (
    <div
      data-testid="weather-today-hero"
      className="mt-8 rounded-2xl overflow-hidden"
      style={{ background: "var(--wofy-surface)", border: "1px solid var(--wofy-border)" }}
    >
      <div className="p-6 sm:p-8 grid sm:grid-cols-[1fr_auto] gap-6 items-start">
        <div>
          <div className="flex items-center gap-3">
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-mono uppercase tracking-widest"
              style={{ background: cat.bg, color: cat.fg }}
            >
              {cat.label}
            </span>
            <span className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>{today.date}</span>
          </div>
          <div className="mt-3 flex items-baseline gap-3">
            <span className="font-display text-6xl">{Math.round(today.temp_max_f ?? 0)}°</span>
            <span className="text-lg" style={{ color: "var(--wofy-text-soft)" }}>
              / {Math.round(today.temp_min_f ?? 0)}°F
            </span>
          </div>
          <p className="mt-1 text-sm font-medium">{today.condition}</p>

          {today.events?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {today.events.map((ev) => (
                <span
                  key={ev}
                  data-testid={`weather-event-${ev}`}
                  className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs"
                  style={{ borderColor: "var(--wofy-border)" }}
                >
                  <span>{EVENT_ICONS[ev] || "•"}</span>
                  <span>{ev.replace(/_/g, " ")}</span>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <Metric icon={Wind} label="Wind" value={today.wind_max_kph != null ? `${Math.round(today.wind_max_kph)} kph` : "—"} />
          <Metric icon={Droplets} label="Humidity" value={today.humidity != null ? `${Math.round(today.humidity)}%` : "—"} />
          <Metric icon={CloudRain} label="Rain" value={`${(today.rain_mm ?? 0).toFixed(1)} mm`} />
          <Metric icon={Sun} label="UV" value={today.uv_index ?? "—"} />
          <Metric icon={Sunrise} label="Sunrise" value={today.sunrise ?? "—"} />
          <Metric icon={Sunset} label="Sunset" value={today.sunset ?? "—"} />
          <Metric icon={Moon} label="Moonrise" value={today.moonrise ?? "—"} />
          <Metric icon={Moon} label="Phase" value={today.moon_phase ?? "—"} />
        </div>
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4 opacity-60" />
      <div>
        <div className="text-[10px] uppercase font-mono tracking-widest" style={{ color: "var(--wofy-text-soft)" }}>{label}</div>
        <div className="text-sm font-medium">{value}</div>
      </div>
    </div>
  );
}

function Comparison({ cmp }) {
  if (!cmp) return null;
  const t = cmp.today || {};
  const cell = (title, value, sub) => (
    <div className="rounded-lg border p-3" style={{ borderColor: "var(--wofy-border)" }}>
      <div className="text-[10px] uppercase font-mono tracking-widest" style={{ color: "var(--wofy-text-soft)" }}>{title}</div>
      <div className="text-lg font-medium mt-1">{value ?? "—"}</div>
      {sub && <div className="text-[11px] mt-0.5" style={{ color: "var(--wofy-text-soft)" }}>{sub}</div>}
    </div>
  );
  const c2f = (c) => (c == null ? null : Math.round(c * 9 / 5 + 32) + "°F");
  return (
    <div className="mt-8" data-testid="weather-comparison">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp className="w-4 h-4" />
        <h2 className="font-display text-2xl">How does today compare?</h2>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {cell("Today's high", c2f(t.temp_max_c), t.rain_mm > 0 ? `${t.rain_mm.toFixed(1)} mm rain` : "")}
        {cell("Normal for today", c2f(cmp.normal_max_c), cmp.sample_years >= 2 ? `Avg of ${cmp.sample_years} yrs` : "Building history…")}
        {cell("Record high", c2f(cmp.record_high?.temp_c), cmp.record_high?.date)}
        {cell("Record low", c2f(cmp.record_low?.temp_c), cmp.record_low?.date)}
      </div>
    </div>
  );
}

function Timeline({ days }) {
  if (!days?.length) return null;
  return (
    <div className="mt-10" data-testid="weather-timeline">
      <div className="flex items-center gap-2 mb-3">
        <CalendarIcon className="w-4 h-4" />
        <h2 className="font-display text-2xl">Last {days.length} days</h2>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-3">
        {days.map((d) => {
          if (d.unavailable) {
            return (
              <div key={d.date} className="min-w-[110px] rounded-lg border p-3 text-xs opacity-60" style={{ borderColor: "var(--wofy-border)" }}>
                <div className="font-mono">{d.date.slice(5)}</div>
                <div className="mt-1" style={{ color: "var(--wofy-text-soft)" }}>No data</div>
              </div>
            );
          }
          const cat = CATEGORY_COLORS[d.category] || CATEGORY_COLORS.pleasant;
          return (
            <div key={d.date} className="min-w-[110px] rounded-lg border p-3" style={{ borderColor: "var(--wofy-border)" }}>
              <div className="font-mono text-xs">{d.date.slice(5)}</div>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="text-xl font-semibold">{Math.round(d.temp_max_f ?? 0)}°</span>
                <span className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>{Math.round(d.temp_min_f ?? 0)}°</span>
              </div>
              <span
                className="mt-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-mono uppercase tracking-widest"
                style={{ background: cat.bg, color: cat.fg }}
              >
                {cat.label}
              </span>
              {d.events?.length > 0 && (
                <div className="mt-1 text-xs" title={d.events.join(", ")}>
                  {d.events.slice(0, 3).map((ev) => <span key={ev}>{EVENT_ICONS[ev] || "•"}</span>)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CalendarGrid({ monthLabel, calendar, onPrev, onNext, coords }) {
  const [popup, setPopup] = useState(null);   // day detail on click

  const openDetail = async (dateStr) => {
    setPopup({ date: dateStr, loading: true });
    try {
      const { data } = await http.get(`/weather-almanac/daily?lat=${coords.lat}&lng=${coords.lng}&date=${dateStr}`);
      setPopup({ ...data, loading: false });
    } catch (e) {
      setPopup({ date: dateStr, loading: false, error: e?.response?.data?.detail || "No data" });
    }
  };

  if (!calendar) return null;
  // Build a 7-col grid with leading blanks so weekday alignment is correct.
  const first = new Date(calendar.year, calendar.month - 1, 1);
  const leading = first.getDay(); // 0=Sun
  const cells = [...Array(leading).fill(null), ...calendar.days];
  const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div className="mt-10" data-testid="weather-calendar">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CalendarIcon className="w-4 h-4" />
          <h2 className="font-display text-2xl">{monthLabel}</h2>
        </div>
        <div className="flex gap-1">
          <button data-testid="cal-prev" onClick={onPrev} className="wofy-btn-ghost p-2"><ChevronLeft className="w-4 h-4" /></button>
          <button data-testid="cal-next" onClick={onNext} className="wofy-btn-ghost p-2"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color: "var(--wofy-text-soft)" }}>
        {weekdays.map((w) => <div key={w} className="text-center py-1">{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c, i) => {
          if (!c) return <div key={`b${i}`} className="aspect-square" />;
          const cat = c.category ? CATEGORY_COLORS[c.category] : null;
          const day = parseInt(c.date.slice(-2), 10);
          return (
            <button
              key={c.date}
              onClick={() => openDetail(c.date)}
              data-testid={`cal-day-${c.date}`}
              className="aspect-square rounded-md border text-xs flex flex-col items-center justify-center transition hover:scale-[1.03]"
              style={{
                borderColor: "var(--wofy-border)",
                background: cat ? cat.bg : "transparent",
                color: cat ? cat.fg : "var(--wofy-text-soft)",
              }}
              title={c.unfetched ? "Tap to load" : `${c.condition || ""}${c.events?.length ? " · " + c.events.join(", ") : ""}`}
            >
              <span className="font-medium">{day}</span>
              {c.temp_max_f != null && <span className="text-[10px] opacity-80">{Math.round(c.temp_max_f)}°</span>}
              {c.events?.length > 0 && (
                <span className="text-[10px] opacity-80">{EVENT_ICONS[c.events[0]] || ""}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-3 flex flex-wrap gap-2 text-[10px] font-mono uppercase tracking-widest">
        {Object.entries(CATEGORY_COLORS).map(([k, v]) => (
          <span key={k} className="inline-flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded" style={{ background: v.bg }} />
            {v.label}
          </span>
        ))}
      </div>

      {/* Detail dialog */}
      {popup && (
        <div
          className="fixed inset-0 flex items-center justify-center p-4 z-50"
          style={{ background: "rgba(0,0,0,0.35)" }}
          onClick={() => setPopup(null)}
          data-testid="cal-day-popup"
        >
          <div
            className="rounded-2xl max-w-md w-full p-6"
            style={{ background: "var(--wofy-bg)", border: "1px solid var(--wofy-border)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <div className="font-display text-2xl">{popup.date}</div>
              <button onClick={() => setPopup(null)} className="wofy-btn-ghost text-xs">Close</button>
            </div>
            {popup.loading && <p className="mt-4 text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</p>}
            {popup.error && <p className="mt-4 text-sm text-amber-700">{popup.error}</p>}
            {!popup.loading && !popup.error && (
              <>
                <p className="mt-2 text-sm">{popup.condition}</p>
                <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <Metric icon={ThermometerSun} label="High" value={popup.temp_max_f != null ? `${Math.round(popup.temp_max_f)}°F` : "—"} />
                  <Metric icon={ThermometerSnowflake} label="Low" value={popup.temp_min_f != null ? `${Math.round(popup.temp_min_f)}°F` : "—"} />
                  <Metric icon={CloudRain} label="Rain" value={`${(popup.rain_mm ?? 0).toFixed(1)} mm`} />
                  <Metric icon={Snowflake} label="Snow" value={`${(popup.snow_cm ?? 0).toFixed(1)} cm`} />
                  <Metric icon={Wind} label="Wind" value={popup.wind_max_kph != null ? `${Math.round(popup.wind_max_kph)} kph` : "—"} />
                  <Metric icon={Droplets} label="Humidity" value={popup.humidity != null ? `${Math.round(popup.humidity)}%` : "—"} />
                </div>
                {popup.events?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {popup.events.map((ev) => (
                      <span key={ev} className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs" style={{ borderColor: "var(--wofy-border)" }}>
                        {EVENT_ICONS[ev] || "•"} {ev.replace(/_/g, " ")}
                      </span>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MonthlySummary({ summary, monthLabel }) {
  if (!summary || summary.no_data) {
    return (
      <div className="mt-10">
        <div className="flex items-center gap-2 mb-3">
          <Trophy className="w-4 h-4" />
          <h2 className="font-display text-2xl">{monthLabel} summary</h2>
        </div>
        <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>
          Not enough data yet. Load more days by scrolling the timeline or opening calendar cells.
        </p>
      </div>
    );
  }
  const c2f = (c) => (c == null ? "—" : Math.round(c * 9 / 5 + 32) + "°F");
  const cells = [
    ["Highest",     c2f(summary.temp_max_c)],
    ["Lowest",      c2f(summary.temp_min_c)],
    ["Average",     c2f(summary.temp_avg_c)],
    ["Humidity",    summary.humidity_avg != null ? `${Math.round(summary.humidity_avg)}%` : "—"],
    ["Rainfall",    `${summary.rain_total_mm.toFixed(1)} mm`],
    ["Snowfall",    `${summary.snow_total_cm.toFixed(1)} cm`],
    ["Rainy days",  summary.days_rainy],
    ["Snowy days",  summary.days_snowy],
    ["Sunny days",  summary.days_sunny],
    ["Stormy days", summary.days_stormy],
    ["Windy days",  summary.days_windy],
    ["Sampled",     `${summary.sample_days} days`],
  ];
  return (
    <div className="mt-10" data-testid="weather-monthly-summary">
      <div className="flex items-center gap-2 mb-3">
        <Trophy className="w-4 h-4" />
        <h2 className="font-display text-2xl">{monthLabel} summary</h2>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {cells.map(([label, value]) => (
          <div key={label} className="rounded-lg border p-3" style={{ borderColor: "var(--wofy-border)" }}>
            <div className="text-[10px] uppercase font-mono tracking-widest" style={{ color: "var(--wofy-text-soft)" }}>{label}</div>
            <div className="text-lg font-medium mt-1">{value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SearchPanel() {
  const [q, setQ] = useState("");
  const [region, setRegion] = useState("");
  const [event, setEvent] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tags, setTags] = useState([]);

  useEffect(() => {
    http.get("/weather-almanac/event-tags").then((r) => setTags(r.data.tags || [])).catch(() => {});
  }, []);

  const run = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (region) params.set("region", region);
      if (event) params.set("event", event);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const { data } = await http.get(`/weather-almanac/locality-search?${params.toString()}`);
      setResults(data);
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-12" data-testid="weather-search">
      <div className="flex items-center gap-2 mb-3">
        <Search className="w-4 h-4" />
        <h2 className="font-display text-2xl">Search the archive</h2>
      </div>
      <form onSubmit={run} className="rounded-2xl border p-4 grid sm:grid-cols-5 gap-3" style={{ borderColor: "var(--wofy-border)" }}>
        <input
          data-testid="weather-search-q"
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Free text (city, condition, event)"
          className="rounded-lg border px-3 py-2 text-sm sm:col-span-2 outline-none"
          style={{ borderColor: "var(--wofy-border)" }}
        />
        <input
          data-testid="weather-search-region"
          value={region} onChange={(e) => setRegion(e.target.value)}
          placeholder="Region / State"
          className="rounded-lg border px-3 py-2 text-sm outline-none"
          style={{ borderColor: "var(--wofy-border)" }}
        />
        <select
          data-testid="weather-search-event"
          value={event} onChange={(e) => setEvent(e.target.value)}
          className="rounded-lg border px-3 py-2 text-sm outline-none"
          style={{ borderColor: "var(--wofy-border)" }}
        >
          <option value="">Any event</option>
          {tags.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
        </select>
        <button type="submit" disabled={busy} data-testid="weather-search-btn" className="wofy-btn-primary text-sm justify-center">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          Search
        </button>
        <input
          type="date"
          data-testid="weather-search-from"
          value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
          className="rounded-lg border px-3 py-2 text-sm outline-none"
          style={{ borderColor: "var(--wofy-border)" }}
        />
        <input
          type="date"
          data-testid="weather-search-to"
          value={dateTo} onChange={(e) => setDateTo(e.target.value)}
          className="rounded-lg border px-3 py-2 text-sm outline-none"
          style={{ borderColor: "var(--wofy-border)" }}
        />
      </form>

      {results && (
        <div className="mt-4" data-testid="weather-search-results">
          <p className="text-xs mb-2" style={{ color: "var(--wofy-text-soft)" }}>
            {results.total} match{results.total === 1 ? "" : "es"} · showing {results.count}
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {results.results.map((r) => {
              const cat = CATEGORY_COLORS[r.category] || CATEGORY_COLORS.pleasant;
              return (
                <div key={r.id} className="rounded-lg border p-3" style={{ borderColor: "var(--wofy-border)" }}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-mono">{r.date}</span>
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-mono uppercase" style={{ background: cat.bg, color: cat.fg }}>{cat.label}</span>
                  </div>
                  <div className="mt-1 text-sm font-medium">{r.city}{r.region ? `, ${r.region}` : ""}</div>
                  <div className="mt-1 text-xs" style={{ color: "var(--wofy-text-soft)" }}>{r.condition}</div>
                  <div className="mt-2 text-xs flex flex-wrap gap-2">
                    <span>H {r.temp_max_c != null ? Math.round(r.temp_max_c * 9 / 5 + 32) + "°F" : "—"}</span>
                    <span>L {r.temp_min_c != null ? Math.round(r.temp_min_c * 9 / 5 + 32) + "°F" : "—"}</span>
                    {(r.rain_mm ?? 0) > 0 && <span>🌧 {r.rain_mm.toFixed(1)} mm</span>}
                    {(r.snow_cm ?? 0) > 0 && <span>❄ {r.snow_cm.toFixed(1)} cm</span>}
                  </div>
                  {r.events?.length > 0 && (
                    <div className="mt-1 text-[10px]">{r.events.slice(0, 4).map((e) => <span key={e} className="mr-1">{EVENT_ICONS[e] || "•"} {e.replace(/_/g, " ")}</span>)}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


// ==================== ALL WEATHER ALERT REPORTS ====================
// Unified feed pulling from AI-almanac records AND citizen weather
// reports. Geographic dropdowns are populated once from
// /weather-almanac/geo-facets. Filter panel + result cards with a
// kind badge so viewers see at a glance which reports are AI-curated
// vs. citizen-submitted.

const KIND_BADGE = {
  almanac: { label: "AI ALMANAC", bg: "#EFF6FF", fg: "#1E3A8A", border: "#BFDBFE" },
  citizen: { label: "COMMUNITY",  bg: "#F0FDF4", fg: "#166534", border: "#BBF7D0" },
};
const SEV_BADGE = {
  Catastrophic: { bg: "#450a0a", fg: "#ffffff" },
  Historic:     { bg: "#7f1d1d", fg: "#ffffff" },
  Severe:       { bg: "#b91c1c", fg: "#ffffff" },
  Major:        { bg: "#ea580c", fg: "#ffffff" },
  Moderate:     { bg: "#f59e0b", fg: "#1f2937" },
  Minor:        { bg: "#84cc16", fg: "#1f2937" },
};

function AllReportsPanel() {
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");
  const [country, setCountry] = useState("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [severity, setSeverity] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [data, setData] = useState({ total: 0, items: [] });
  const [facets, setFacets] = useState({ countries: [], states: [], cities: [] });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (q)         params.set("q", q);
      if (kind)      params.set("kind", kind);
      if (country)   params.set("country", country);
      if (state)     params.set("state", state);
      if (city)      params.set("city", city);
      if (severity)  params.set("severity", severity);
      if (dateFrom)  params.set("date_from", dateFrom);
      if (dateTo)    params.set("date_to", dateTo);
      params.set("limit", "60");
      const { data } = await http.get(`/weather-almanac/all-reports?${params.toString()}`);
      setData(data);
    } finally { setBusy(false); }
  }, [q, kind, country, state, city, severity, dateFrom, dateTo]);

  useEffect(() => {
    http.get("/weather-almanac/geo-facets").then((r) => setFacets(r.data)).catch(() => {});
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section id="all-weather-reports" className="mt-14 scroll-mt-24" data-testid="all-weather-reports">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="w-5 h-5 text-red-600" />
        <h2 className="font-display text-2xl">All Weather Alert Reports</h2>
        <span className="text-xs opacity-60">
          {data.total} report{data.total === 1 ? "" : "s"} in the archive
        </span>
      </div>
      <p className="text-sm mb-3" style={{ color: "var(--wofy-text-soft)" }}>
        Every AI-generated almanac record and every community-submitted weather report,
        in one filterable list. Bookmark this section:
        <a href="#all-weather-reports" className="underline ml-1" style={{ color: "var(--wofy-primary)" }}>
          #all-weather-reports
        </a>
      </p>

      <form
        onSubmit={(e) => { e.preventDefault(); load(); }}
        className="rounded-2xl border p-4 grid grid-cols-1 sm:grid-cols-4 gap-3"
        style={{ borderColor: "var(--wofy-border)" }}
      >
        <input
          data-testid="all-reports-q"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search — title, city, keyword…"
          className="rounded-lg border px-3 py-2 text-sm sm:col-span-2 outline-none"
          style={{ borderColor: "var(--wofy-border)" }}
        />
        <select
          data-testid="all-reports-kind"
          value={kind} onChange={(e) => setKind(e.target.value)}
          className="rounded-lg border px-3 py-2 text-sm outline-none bg-white"
          style={{ borderColor: "var(--wofy-border)" }}
        >
          <option value="all">All types</option>
          <option value="almanac">AI Almanac only</option>
          <option value="citizen">Community only</option>
        </select>
        <select
          data-testid="all-reports-severity"
          value={severity} onChange={(e) => setSeverity(e.target.value)}
          className="rounded-lg border px-3 py-2 text-sm outline-none bg-white"
          style={{ borderColor: "var(--wofy-border)" }}
        >
          <option value="">Any severity</option>
          {Object.keys(SEV_BADGE).map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        <select
          data-testid="all-reports-country"
          value={country} onChange={(e) => setCountry(e.target.value)}
          className="rounded-lg border px-3 py-2 text-sm outline-none bg-white"
          style={{ borderColor: "var(--wofy-border)" }}
        >
          <option value="">All countries</option>
          {facets.countries.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select
          data-testid="all-reports-state"
          value={state} onChange={(e) => setState(e.target.value)}
          className="rounded-lg border px-3 py-2 text-sm outline-none bg-white"
          style={{ borderColor: "var(--wofy-border)" }}
        >
          <option value="">All states / regions</option>
          {facets.states.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          data-testid="all-reports-city"
          value={city} onChange={(e) => setCity(e.target.value)}
          className="rounded-lg border px-3 py-2 text-sm outline-none bg-white"
          style={{ borderColor: "var(--wofy-border)" }}
        >
          <option value="">All cities</option>
          {facets.cities.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button
          type="submit"
          disabled={busy}
          data-testid="all-reports-apply"
          className="wofy-btn-primary text-sm justify-center"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          Apply filters
        </button>

        <input
          type="date" data-testid="all-reports-from"
          value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
          className="rounded-lg border px-3 py-2 text-sm outline-none"
          style={{ borderColor: "var(--wofy-border)" }}
        />
        <input
          type="date" data-testid="all-reports-to"
          value={dateTo} onChange={(e) => setDateTo(e.target.value)}
          className="rounded-lg border px-3 py-2 text-sm outline-none"
          style={{ borderColor: "var(--wofy-border)" }}
        />
        {(q || kind !== "all" || country || state || city || severity || dateFrom || dateTo) && (
          <button
            type="button"
            onClick={() => {
              setQ(""); setKind("all"); setCountry(""); setState(""); setCity("");
              setSeverity(""); setDateFrom(""); setDateTo("");
              setTimeout(load, 0);
            }}
            className="wofy-btn-ghost text-sm"
            data-testid="all-reports-clear"
          >
            Clear filters
          </button>
        )}
      </form>

      <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="all-reports-results">
        {busy && data.items.length === 0 && (
          <div className="col-span-full py-8 text-center opacity-60" data-testid="all-reports-loading">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
          </div>
        )}
        {!busy && data.items.length === 0 && (
          <div className="col-span-full py-8 text-center opacity-60" data-testid="all-reports-empty">
            No reports match your filters. Try clearing the geo/date filters.
          </div>
        )}
        {data.items.map((r) => {
          const kb = KIND_BADGE[r.kind] || KIND_BADGE.citizen;
          const sb = SEV_BADGE[r.severity] || null;
          return (
            <Link
              key={`${r.kind}-${r.id}`}
              to={r.url}
              className="block rounded-lg border p-3 hover:shadow-md transition"
              style={{ borderColor: "var(--wofy-border)", background: "white" }}
              data-testid={`all-reports-item-${r.kind}-${r.id}`}
            >
              <div className="flex items-center gap-2 flex-wrap text-[10px]">
                <span
                  className="px-1.5 py-0.5 rounded font-mono uppercase"
                  style={{ background: kb.bg, color: kb.fg, border: `1px solid ${kb.border}` }}
                >
                  {kb.label}
                </span>
                {sb && (
                  <span
                    className="px-1.5 py-0.5 rounded font-mono uppercase"
                    style={{ background: sb.bg, color: sb.fg }}
                  >
                    {r.severity}
                  </span>
                )}
                <span className="ml-auto font-mono opacity-60">{r.date || ""}</span>
              </div>
              <div className="mt-2 text-sm font-semibold" style={{ color: "var(--wofy-text)" }}>
                {r.title || "Untitled report"}
              </div>
              <div className="text-xs mt-0.5" style={{ color: "var(--wofy-text-soft)" }}>
                {[r.city, r.state, r.country].filter(Boolean).join(", ") || "—"}
              </div>
              {r.summary && (
                <div className="text-xs mt-1.5 line-clamp-2" style={{ color: "var(--wofy-text-soft)" }}>
                  {r.summary}
                </div>
              )}
              {r.event_type && (
                <div className="text-[10px] mt-2 opacity-70">{r.event_type}</div>
              )}
            </Link>
          );
        })}
      </div>
    </section>
  );
}




// ==================== NEW SECTIONS (Phase 2A) ====================

const SEVERE_TAGS = new Set([
  "tornado", "hurricane", "cyclone", "blizzard", "ice_storm",
  "flood_risk", "extreme_heat", "extreme_cold", "thunderstorm",
  "high_wind", "hail", "snowstorm", "wildfire_smoke", "dust_storm",
]);

function SevereBanner({ events = [], category }) {
  const severe = (events || []).filter((e) => SEVERE_TAGS.has(e));
  if (severe.length === 0 && category !== "severe" && category !== "storm") return null;
  return (
    <div
      data-testid="weather-severe-banner"
      className="mt-6 rounded-xl border-2 p-4 flex items-start gap-3"
      style={{
        borderColor: "#dc2626",
        background: "#fef2f2",
        color: "#7f1d1d",
      }}
    >
      <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
      <div className="flex-1">
        <div className="font-medium">Severe weather in effect for your locality</div>
        <div className="text-xs mt-1">
          Active: {severe.length > 0 ? severe.map((e) => e.replace(/_/g, " ")).join(" · ") : "storm conditions"}
        </div>
        <div className="text-xs mt-2 opacity-80">
          Take precautions and check community reports below for real-time on-the-ground updates.
        </div>
      </div>
    </div>
  );
}

const TONE_COLORS = {
  good:    { bg: "#dcfce7", fg: "#166534", border: "#86efac" },
  info:    { bg: "#dbeafe", fg: "#1e40af", border: "#93c5fd" },
  caution: { bg: "#fef3c7", fg: "#92400e", border: "#fcd34d" },
  warning: { bg: "#fee2e2", fg: "#991b1b", border: "#fca5a5" },
};

const LEVEL_COLORS = {
  low:      { bg: "#dcfce7", fg: "#166534" },
  moderate: { bg: "#fef3c7", fg: "#92400e" },
  high:     { bg: "#fee2e2", fg: "#991b1b" },
};

function AdvisoryCard({ tone = "info", headline, detail, categoryLabel, topicKey }) {
  const c = TONE_COLORS[tone] || TONE_COLORS.info;
  const inner = (
    <>
      {categoryLabel && (
        <div className="text-[10px] font-mono uppercase tracking-widest opacity-70">{categoryLabel}</div>
      )}
      <div className="text-sm font-medium mt-0.5">{headline}</div>
      {detail && <div className="text-xs mt-1 opacity-90">{detail}</div>}
    </>
  );
  const style = { borderColor: c.border, background: c.bg, color: c.fg };
  if (topicKey) {
    return (
      <Link
        to={`/weather/topic/${topicKey}`}
        className="rounded-lg border p-3 block hover:brightness-95 transition"
        style={style}
        data-testid={`advisory-card-${topicKey}`}
      >
        {inner}
      </Link>
    );
  }
  return <div className="rounded-lg border p-3" style={style}>{inner}</div>;
}

function Advisories({ advisories, onReport }) {
  if (!advisories) return null;
  return (
    <div id="weather-advisories" className="mt-10" data-testid="weather-advisories">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Sprout className="w-4 h-4" />
          <h2 className="font-display text-2xl">Today's advisories</h2>
        </div>
        {onReport ? (
          <button
            type="button"
            onClick={onReport}
            className="wofy-btn-primary text-sm"
            data-testid="weather-report-cta-advisories"
          >
            <Camera className="w-4 h-4" /> 🌦️ Report Local Weather
          </button>
        ) : null}
      </div>

      {/* Farmer's Dashboard */}
      <div className="mt-4">
        <div className="flex items-center gap-2 mb-2">
          <Sprout className="w-3.5 h-3.5" />
          <h3 className="text-sm font-medium uppercase tracking-widest font-mono">
            <Link to="/weather/topic/farmer" className="hover:underline" data-testid="farmer-topic-link">
              Farmer's Dashboard
            </Link>
          </h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {(advisories.farmer || []).map((a, i) => (
            <AdvisoryCard
              key={`farmer-${i}`}
              tone={a.tone}
              headline={a.headline}
              detail={a.detail}
              categoryLabel={a.category}
              topicKey={(a.category || "").toLowerCase().trim().replace(/\s+/g, "_") || "farmer"}
            />
          ))}
        </div>
      </div>

      {/* Gardening */}
      {advisories.gardening?.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center gap-2 mb-2">
            <Flower2 className="w-3.5 h-3.5" />
            <h3 className="text-sm font-medium uppercase tracking-widest font-mono">
              <Link to="/weather/topic/gardening" className="hover:underline" data-testid="gardening-topic-link">
                Gardening
              </Link>
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {advisories.gardening.map((a, i) => (
              <AdvisoryCard key={`garden-${i}`} tone={a.tone} headline={a.headline} detail={a.detail} categoryLabel={a.category} topicKey="gardening" />
            ))}
          </div>
        </div>
      )}

      {/* Pests + Allergy */}
      <div className="mt-6 grid md:grid-cols-2 gap-6">
        {advisories.pests?.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Bug className="w-3.5 h-3.5" />
              <h3 className="text-sm font-medium uppercase tracking-widest font-mono">
                <Link to="/weather/topic/pests" className="hover:underline" data-testid="pests-topic-link">
                  Pest Forecast
                </Link>
              </h3>
            </div>
            <div className="space-y-2">
              {advisories.pests.map((p, i) => {
                const c = LEVEL_COLORS[p.level] || LEVEL_COLORS.moderate;
                return (
                  <div key={i} className="flex items-center justify-between rounded-lg border p-3" style={{ borderColor: "var(--wofy-border)" }}>
                    <div>
                      <div className="text-sm font-medium">{p.pest}</div>
                      <div className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>{p.detail}</div>
                    </div>
                    <span className="text-[10px] uppercase font-mono px-2 py-0.5 rounded-full" style={{ background: c.bg, color: c.fg }}>{p.level}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {advisories.allergy?.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-3.5 h-3.5" />
              <h3 className="text-sm font-medium uppercase tracking-widest font-mono">
                <Link to="/weather/topic/allergy" className="hover:underline" data-testid="allergy-topic-link">
                  Allergy Forecast
                </Link>
              </h3>
            </div>
            <div className="space-y-2">
              {advisories.allergy.map((a, i) => {
                const c = LEVEL_COLORS[a.level] || LEVEL_COLORS.moderate;
                return (
                  <div key={i} className="flex items-center justify-between rounded-lg border p-3" style={{ borderColor: "var(--wofy-border)" }}>
                    <div>
                      <div className="text-sm font-medium">{a.category}</div>
                      <div className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>{a.detail}</div>
                    </div>
                    <span className="text-[10px] uppercase font-mono px-2 py-0.5 rounded-full" style={{ background: c.bg, color: c.fg }}>{a.level}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Outdoor Activities */}
      {advisories.activities?.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-3.5 h-3.5" />
            <h3 className="text-sm font-medium uppercase tracking-widest font-mono">
              <Link to="/weather/topic/activities" className="hover:underline" data-testid="activities-topic-link">
                Outdoor Activities Today
              </Link>
            </h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {advisories.activities.map((a, i) => (
              <div
                key={i}
                className="rounded-lg border p-3"
                style={{
                  borderColor: a.rating === "good" ? "#86efac" : "#fca5a5",
                  background: a.rating === "good" ? "#dcfce7" : "#fef2f2",
                  color: a.rating === "good" ? "#166534" : "#7f1d1d",
                }}
              >
                <div className="text-sm font-medium">{a.activity}</div>
                <div className="text-[10px] mt-1 opacity-80">{a.note}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Fishing + Moon Gardening + Drought */}
      <div className="mt-6 grid md:grid-cols-3 gap-6">
        {advisories.fishing && <FishingCard fishing={advisories.fishing} />}
        {advisories.moon_gardening?.length > 0 && <MoonGardeningCard notes={advisories.moon_gardening} />}
        {advisories.drought && <DroughtCard drought={advisories.drought} />}
      </div>
    </div>
  );
}

function FishingCard({ fishing }) {
  const bar = (fishing.score || 0);
  return (
    <Link to="/weather/topic/fishing" className="rounded-2xl border p-4 block hover:brightness-95 transition" style={{ borderColor: "var(--wofy-border)" }} data-testid="fishing-topic-link">
      <div className="flex items-center gap-2 mb-2">
        <Fish className="w-4 h-4" />
        <h3 className="text-sm font-medium uppercase tracking-widest font-mono">Fishing</h3>
        <span
          className="ml-auto text-[10px] uppercase font-mono px-2 py-0.5 rounded-full"
          style={{
            background: fishing.level === "excellent" ? "#dcfce7" : fishing.level === "good" ? "#dbeafe" : fishing.level === "fair" ? "#fef3c7" : "#fee2e2",
            color: fishing.level === "excellent" ? "#166534" : fishing.level === "good" ? "#1e40af" : fishing.level === "fair" ? "#92400e" : "#991b1b",
          }}
        >
          {fishing.level}
        </span>
      </div>
      <div className="h-2 rounded-full overflow-hidden mb-3" style={{ background: "var(--wofy-border)" }}>
        <div className="h-full" style={{ width: `${bar}%`, background: "var(--wofy-primary)" }} />
      </div>
      <div className="text-xs mb-2" style={{ color: "var(--wofy-text-soft)" }}>Best hours: {fishing.best_hours}</div>
      <ul className="text-xs space-y-1">
        {fishing.notes?.map((n, i) => <li key={i}>• {n}</li>)}
      </ul>
    </Link>
  );
}

function MoonGardeningCard({ notes }) {
  return (
    <Link to="/weather/topic/moon_gardening" className="rounded-2xl border p-4 block hover:brightness-95 transition" style={{ borderColor: "var(--wofy-border)" }} data-testid="moon-gardening-topic-link">
      <div className="flex items-center gap-2 mb-3">
        <MoonIcon className="w-4 h-4" />
        <h3 className="text-sm font-medium uppercase tracking-widest font-mono">Moon Gardening</h3>
      </div>
      <ul className="text-xs space-y-2">
        {notes.map((n, i) => (
          <li key={i}>
            <div className="font-medium">{n.category}</div>
            <div className="opacity-80">{n.detail}</div>
          </li>
        ))}
      </ul>
    </Link>
  );
}

function DroughtCard({ drought }) {
  const bg = {
    severe_drought: "#fee2e2",
    dry:            "#fef3c7",
    normal:         "#dcfce7",
    wet:            "#dbeafe",
  }[drought.status] || "#f3f4f6";
  const fg = {
    severe_drought: "#991b1b",
    dry:            "#92400e",
    normal:         "#166534",
    wet:            "#1e40af",
  }[drought.status] || "#111827";
  return (
    <Link to="/weather/topic/drought" className="rounded-2xl border p-4 block hover:brightness-95 transition" style={{ borderColor: "var(--wofy-border)", background: bg, color: fg }} data-testid="drought-topic-link">
      <div className="flex items-center gap-2 mb-2">
        <CloudDrizzle className="w-4 h-4" />
        <h3 className="text-sm font-medium uppercase tracking-widest font-mono">30-Day Rainfall</h3>
      </div>
      <div className="text-sm font-medium">{drought.headline}</div>
      <div className="text-xs mt-1 opacity-90">{drought.detail}</div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
        <div><span className="opacity-70">Total 30d:</span> <b>{drought.total_mm_30d} mm</b></div>
        <div><span className="opacity-70">Longest dry:</span> <b>{drought.longest_dry_streak_days} days</b></div>
      </div>
    </Link>
  );
}

// ---------------- Community Reports ----------------

const REPORT_ICONS = {
  flooding: "🌊", flash_flood: "🌊", hail: "🌨", heavy_rain: "🌧",
  thunderstorm: "⛈", lightning: "⚡", tornado: "🌪", hurricane: "🌀",
  cyclone: "🌀", high_wind: "💨", wind_damage: "💨", downed_tree: "🌳",
  snow: "🌨", heavy_snow: "🌨", blizzard: "❄", ice: "🧊", ice_storm: "🧊",
  fog: "🌫", dust_storm: "🌪", wildfire: "🔥", smoke: "🔥",
  power_outage: "💡", road_closure: "🚧", landslide: "🏞",
  extreme_heat: "🔥", extreme_cold: "🥶", other: "•",
};

function CommunityReports({ coords, today, openTick, onReport }) {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [types, setTypes] = useState([]);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await http.get(`/weather-reports?lat=${coords.lat}&lng=${coords.lng}&radius_km=50&hours=72&limit=30`);
      setReports(data.reports || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => {
    refresh();
    http.get("/weather-reports/meta/types").then((r) => setTypes(r.data.types || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coords.lat, coords.lng]);

  // Parent bumps `openTick` when any "Report Local Weather" CTA on the
  // page is clicked. Skip the initial 0 so the form doesn't auto-open
  // on the first mount.
  useEffect(() => {
    if (openTick && openTick > 0) setShowForm(true);
  }, [openTick]);

  const verify = async (id) => {
    if (!user) { toast.error("Sign in to verify reports."); return; }
    try {
      await http.post(`/weather-reports/${id}/verify`, { kind: "confirm" });
      toast.success("Thanks for verifying!");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't verify.");
    }
  };

  return (
    <div id="community-reports" className="mt-12" data-testid="weather-community-reports">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4" />
          <h2 className="font-display text-2xl">Community reports nearby</h2>
        </div>
        {user ? (
          <button
            data-testid="submit-weather-report-btn"
            onClick={onReport || (() => setShowForm(true))}
            className="wofy-btn-primary text-sm"
          >
            <Camera className="w-4 h-4" /> 🌦️ Report Local Weather
          </button>
        ) : (
          <button
            onClick={onReport || (() => toast.error("Sign in to post a report."))}
            data-testid="submit-weather-report-btn"
            className="wofy-btn-primary text-sm"
          >
            <Camera className="w-4 h-4" /> 🌦️ Report Local Weather
          </button>
        )}
      </div>

      {loading && <p className="text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>}
      {!loading && reports.length === 0 && (
        <p className="text-sm rounded-lg border p-4" style={{ color: "var(--wofy-text-soft)", borderColor: "var(--wofy-border)" }}>
          No reports in the last 72 hours within 50 km. Be the first to share what you're seeing.
        </p>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {reports.map((r) => (
          <div key={r.id} className="rounded-lg border overflow-hidden" style={{ borderColor: "var(--wofy-border)" }}>
            {r.photo_urls?.length > 0 && (
              <div className="aspect-video bg-black/5 overflow-hidden">
                <img src={r.photo_urls[0]} alt={r.type} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="p-3">
              <div className="flex items-center gap-2 text-xs">
                <span className="text-lg">{REPORT_ICONS[r.type] || "•"}</span>
                <span className="font-medium">{r.type.replace(/_/g, " ")}</span>
                <span className="ml-auto text-[10px] font-mono uppercase px-2 py-0.5 rounded-full" style={{ background: "#f3f4f6" }}>
                  {r.severity}
                </span>
              </div>
              {r.description && <p className="text-xs mt-2" style={{ color: "var(--wofy-text-soft)" }}>{r.description}</p>}
              <div className="mt-2 flex items-center justify-between text-[10px] font-mono uppercase tracking-widest" style={{ color: "var(--wofy-text-soft)" }}>
                <span>{r.city || `${r.lat.toFixed(2)},${r.lng.toFixed(2)}`}</span>
                <span>{new Date(r.created_at).toLocaleString()}</span>
              </div>
              <div className="mt-3 flex items-center justify-between">
                <div className="text-xs flex items-center gap-1">
                  {r.verified && <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />}
                  <span style={{ color: r.verified ? "#065f46" : "var(--wofy-text-soft)" }}>
                    {r.verifications?.length || 0} verification{r.verifications?.length === 1 ? "" : "s"}
                  </span>
                </div>
                {user && user.id !== r.user_id && (
                  <button
                    onClick={() => verify(r.id)}
                    data-testid={`verify-report-${r.id}`}
                    disabled={r.verifications?.includes(user.id)}
                    className="text-xs inline-flex items-center gap-1 rounded-md border px-2 py-1 disabled:opacity-50"
                    style={{ borderColor: "var(--wofy-border)" }}
                  >
                    <ThumbsUp className="w-3.5 h-3.5" />
                    {r.verifications?.includes(user.id) ? "Verified" : "Verify"}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <ReportForm
          coords={coords}
          today={today}
          types={types}
          onClose={() => setShowForm(false)}
          onSubmitted={() => { setShowForm(false); refresh(); }}
        />
      )}
    </div>
  );
}

// LocalStorage key for form drafts. Keyed by user_id so different users
// on the same browser get their own draft.
const _draftKey = (uid) => `wofy.weather_report_draft.${uid || "anon"}`;

function ReportForm({ coords, today, types, onClose, onSubmitted }) {
  const { user } = useAuth();
  const draftKey = _draftKey(user?.id);

  // ---- Hydrate from localStorage on mount ----
  const draft = useMemo(() => {
    try {
      const raw = localStorage.getItem(draftKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, [draftKey]);

  const [type, setType] = useState(draft?.type || "flooding");
  const [description, setDescription] = useState(draft?.description || "");
  const [notes, setNotes] = useState(draft?.notes || "");
  const [severity, setSeverity] = useState(draft?.severity || "moderate");
  const [photos, setPhotos] = useState(draft?.photos || []);
  const [videoUrls, setVideoUrls] = useState(draft?.videoUrls?.length ? draft.videoUrls : [""]);
  const [busy, setBusy] = useState(false);
  const [restored, setRestored] = useState(!!draft);
  // Location the user is actually posting FROM — can differ from the
  // weather-almanac locality they're currently browsing. Auto-detected
  // from GPS by LocationConfirmBar; user can Change without touching
  // their global set-location bar.
  const [postLoc, setPostLoc] = useState(null);
  const fileRef = useRef(null);

  // Toast once when a draft was restored.
  useEffect(() => {
    if (restored) {
      toast.info("Draft restored — pick up where you left off.");
      setRestored(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Persist draft on every field change ----
  useEffect(() => {
    try {
      const doc = { type, description, notes, severity, photos, videoUrls };
      // Skip saving empty drafts so we don't churn localStorage.
      const anyContent =
        description.trim() || notes.trim() || photos.length > 0 ||
        videoUrls.some((v) => (v || "").trim());
      if (anyContent) {
        localStorage.setItem(draftKey, JSON.stringify(doc));
      }
    } catch { /* quota / private mode — silently ignore */ }
  }, [draftKey, type, description, notes, severity, photos, videoUrls]);

  const onFile = (e) => {
    const files = [...(e.target.files || [])].slice(0, 5);
    Promise.all(files.map((f) => new Promise((res) => {
      const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f);
    }))).then((urls) => setPhotos((p) => [...p, ...urls].slice(0, 5)));
  };

  const updateVideoAt = (i, v) => {
    setVideoUrls((prev) => prev.map((x, j) => (i === j ? v : x)));
  };
  const addVideoRow = () => setVideoUrls((prev) => (prev.length >= 3 ? prev : [...prev, ""]));
  const removeVideoAt = (i) => setVideoUrls((prev) => prev.filter((_, j) => j !== i) || [""]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Prefer the user-confirmed post-time location; fall back to the
      // browsing locality if they've somehow bypassed the bar (shouldn't
      // happen because it always emits after mount).
      const lat = postLoc?.lat ?? coords.lat;
      const lng = postLoc?.lng ?? coords.lng;
      const cleanVideoUrls = videoUrls.map((v) => (v || "").trim()).filter(Boolean);
      await http.post("/weather-reports", {
        type,
        description: description || null,
        notes: notes || null,
        severity,
        lat, lng,
        city:    postLoc?.city    || today?.city,
        region:  postLoc?.state   || today?.region,
        country: postLoc?.country || today?.country,
        photos_base64: photos,
        video_urls: cleanVideoUrls,
      });
      toast.success("Report posted. Thank you!");
      // Clear the persisted draft on successful submit.
      try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
      onSubmitted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't post report.");
    } finally { setBusy(false); }
  };

  const discardDraft = () => {
    try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
    setType("flooding"); setDescription(""); setNotes("");
    setSeverity("moderate"); setPhotos([]); setVideoUrls([""]);
    toast.success("Draft cleared.");
  };

  return (
    <div
      className="fixed inset-0 flex items-start justify-center p-4 pt-24 sm:pt-28 pb-8 z-50 overflow-y-auto"
      style={{ background: "rgba(0,0,0,0.45)", backdropFilter: "blur(2px)" }}
      onClick={onClose}
      data-testid="weather-report-form"
    >
      <div
        className="rounded-2xl max-w-lg w-full p-6 max-h-[calc(100vh-8rem)] overflow-y-auto shadow-2xl"
        style={{ background: "var(--wofy-bg)", border: "1px solid var(--wofy-border)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <div className="font-display text-2xl">🌦️ Report weather nearby</div>
          <button onClick={onClose} className="wofy-btn-ghost text-xs" data-testid="report-close-btn">Close</button>
        </div>
        <p className="mt-1 text-xs" style={{ color: "var(--wofy-text-soft)" }}>
          Your draft is saved automatically — you can close this and come back later.
        </p>
        <form onSubmit={submit} className="mt-4 space-y-3">
          {/* Confirmable posting location — GPS by default, override
              via Country / State / City typeahead. Does NOT touch the
              user's global set-location bar. */}
          <LocationConfirmBar
            label="Where are you posting this report from?"
            onConfirm={setPostLoc}
            testid="weather-post-loc"
          />
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-widest" style={{ color: "var(--wofy-text-soft)" }}>Type</span>
            <select
              data-testid="report-type"
              value={type} onChange={(e) => setType(e.target.value)}
              className="mt-1 w-full rounded-lg border px-3 py-2 text-sm" style={{ borderColor: "var(--wofy-border)" }}
            >
              {types.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-widest" style={{ color: "var(--wofy-text-soft)" }}>Severity</span>
            <select
              data-testid="report-severity"
              value={severity} onChange={(e) => setSeverity(e.target.value)}
              className="mt-1 w-full rounded-lg border px-3 py-2 text-sm" style={{ borderColor: "var(--wofy-border)" }}
            >
              <option value="mild">Mild</option>
              <option value="moderate">Moderate</option>
              <option value="severe">Severe</option>
              <option value="critical">Critical</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-widest" style={{ color: "var(--wofy-text-soft)" }}>Description (short)</span>
            <textarea
              data-testid="report-description"
              value={description} onChange={(e) => setDescription(e.target.value)}
              rows={2} maxLength={1000}
              placeholder="What are you seeing? e.g. Street flooded past knee-deep on Main St."
              className="mt-1 w-full rounded-lg border px-3 py-2 text-sm" style={{ borderColor: "var(--wofy-border)" }}
            />
          </label>
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-widest flex items-center gap-1" style={{ color: "var(--wofy-text-soft)" }}>
              <StickyNote className="w-3 h-3" /> Notes (long-form)
            </span>
            <textarea
              data-testid="report-notes"
              value={notes} onChange={(e) => setNotes(e.target.value)}
              rows={4} maxLength={2000}
              placeholder="Add more context — timing, community impact, cleanup progress, anything neighbours should know."
              className="mt-1 w-full rounded-lg border px-3 py-2 text-sm" style={{ borderColor: "var(--wofy-border)" }}
            />
            <div className="text-[10px] mt-0.5" style={{ color: "var(--wofy-text-soft)" }}>{notes.length} / 2000</div>
          </label>
          <div>
            <span className="text-xs font-mono uppercase tracking-widest flex items-center gap-1" style={{ color: "var(--wofy-text-soft)" }}>
              <Camera className="w-3 h-3" /> Photos (up to 5)
            </span>
            <input ref={fileRef} data-testid="report-photos" type="file" accept="image/*" multiple onChange={onFile} className="mt-1 text-sm block" />
            {photos.length > 0 && (
              <div className="mt-2 grid grid-cols-5 gap-1">
                {photos.map((p, i) => (
                  <div key={i} className="relative aspect-square rounded overflow-hidden">
                    <img src={p} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => setPhotos(photos.filter((_, j) => j !== i))}
                      className="absolute top-0 right-0 bg-black/60 text-white text-[10px] px-1"
                      data-testid={`remove-photo-${i}`}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <span className="text-xs font-mono uppercase tracking-widest flex items-center gap-1" style={{ color: "var(--wofy-text-soft)" }}>
              <Video className="w-3 h-3" /> Video links (up to 3 — YouTube / Vimeo / Facebook / Instagram)
            </span>
            <div className="mt-1 space-y-2">
              {videoUrls.map((v, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Link2 className="w-3.5 h-3.5 opacity-60" />
                  <input
                    type="url"
                    inputMode="url"
                    value={v}
                    onChange={(e) => updateVideoAt(i, e.target.value)}
                    placeholder="https://youtube.com/watch?v=… or https://facebook.com/… or https://instagram.com/p/…"
                    className="flex-1 rounded-lg border px-3 py-1.5 text-sm"
                    style={{ borderColor: "var(--wofy-border)" }}
                    data-testid={`video-url-${i}`}
                  />
                  {videoUrls.length > 1 ? (
                    <button
                      type="button"
                      onClick={() => removeVideoAt(i)}
                      className="text-xs opacity-60 hover:opacity-100"
                      data-testid={`video-remove-${i}`}
                      title="Remove"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  ) : null}
                </div>
              ))}
              {videoUrls.length < 3 ? (
                <button
                  type="button"
                  onClick={addVideoRow}
                  className="text-xs underline"
                  style={{ color: "var(--wofy-primary)" }}
                  data-testid="video-add-row"
                >
                  + Add another video link
                </button>
              ) : null}
            </div>
          </div>
          <div className="flex items-center gap-2 pt-2">
            <button
              data-testid="submit-report-btn"
              disabled={busy}
              type="submit"
              className="flex-1 wofy-btn-primary justify-center text-sm disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {busy ? "Posting…" : "Post report"}
            </button>
            <button
              type="button"
              onClick={discardDraft}
              disabled={busy}
              className="wofy-btn-ghost text-xs"
              data-testid="discard-draft-btn"
            >
              Discard draft
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
